/**
 * Content formatting utilities for chat messages
 */

/**
 * Format content to ensure it's always a readable string
 * @param {any} content - The content to format
 * @returns {string} Formatted content string
 */
export function formatContent(content) {
    if (typeof content === 'string') {
        return content;
    } else if (typeof content === 'object' && content !== null) {
        // Pretty format JSON objects
        return JSON.stringify(content, null, 2);
    } else if (content === null || content === undefined) {
        return 'Keine Antwort erhalten';
    } else {
        return String(content);
    }
}

/**
 * Check if content appears to be JSON
 * @param {any} content - The content to check
 * @returns {boolean} True if content looks like JSON
 */
export function isJsonLike(content) {
    if (typeof content === 'object' && content !== null) {
        return true;
    }
    
    if (typeof content === 'string') {
        const trimmed = content.trim();
        return (trimmed.startsWith('{') && trimmed.endsWith('}')) || 
               (trimmed.startsWith('[') && trimmed.endsWith(']'));
    }
    
    return false;
}

/**
 * Safely parse JSON content
 * @param {string} content - The content to parse
 * @returns {Object|null} Parsed JSON or null if invalid
 */
export function safeJsonParse(content) {
    try {
        return JSON.parse(content);
    } catch (error) {
        return null;
    }
}

/**
 * Format content for display with enhanced JSON handling
 * @param {any} content - The content to format
 * @param {boolean} isAI - Whether this is an AI message
 * @returns {string} Formatted content for display
 */
export function formatForDisplay(content, isAI = false) {
    const formatted = formatContent(content);
    
    // If it's AI content and looks like JSON, wrap it in a code block for better display
    if (isAI && isJsonLike(formatted)) {
        return `\`\`\`json\n${formatted}\n\`\`\``;
    }
    
    return formatted;
}

/**
 * Validate and clean message content
 * @param {any} content - The content to validate
 * @returns {string} Clean, validated content
 */
export function validateMessageContent(content) {
    const formatted = formatContent(content);
    
    // Remove any potentially harmful content
    const cleaned = formatted
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '[Script removed for security]')
        .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '[Iframe removed for security]');
    
    return cleaned;
}
